import { AppWindow, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { AppsReportItem } from "@/api/queries";
import { CategoryDot } from "@/components/common/Category";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDuration } from "@/lib/format";

type TopAppsCardProps = {
  items: AppsReportItem[] | undefined;
  lang: string;
  /** Link to the full report. */
  moreHref: string;
  description: string;
};

/** The applications and sites with the most time, with their categories. */
export function TopAppsCard({ items, lang, moreHref, description }: TopAppsCardProps) {
  const { t } = useTranslation();
  const max = items?.[0]?.seconds ?? 1;
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader className="flex-row items-start justify-between gap-3 pb-4">
        <div>
          <CardTitle>{t("dashboard.topApps")}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Link to={moreHref} className="shrink-0 font-medium text-primary hover:underline">
          {t("dashboard.allApps")}
        </Link>
      </CardHeader>
      {!items ? (
        <div className="flex flex-col gap-3 px-5 pb-5">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : items.length === 0 ? (
        <p className="px-5 pb-5 text-muted">{t("timeline.appsEmpty")}</p>
      ) : (
        <ul>
          {items.map((a) => (
            <li key={`${a.kind}:${a.key}`} className="flex items-center gap-3 border-t px-5 py-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-muted">
                {a.kind === "url" ? (
                  <Globe className="size-3.5" aria-hidden="true" />
                ) : (
                  <AppWindow className="size-3.5" aria-hidden="true" />
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <CategoryDot category={a.category} />
                    <span className="truncate font-medium" title={a.key}>
                      {a.name}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium tabular-nums">
                    {formatDuration(a.seconds, lang)}
                  </span>
                </div>
                <div className="h-1 rounded-full bg-surface-muted">
                  <div
                    className="h-1 rounded-full bg-primary-solid"
                    style={{ width: `${Math.max(2, (a.seconds / max) * 100)}%` }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
